import { useState, useEffect, useRef, useCallback } from "react";
import { useTheme } from "../../../theme";
import { BRAND_COLORS } from "../../../theme/colors.js";

export default function StealthSection() {
  const t = useTheme();
  const containerRef = useRef(null);
  const canvasRef = useRef(null);
  const headlineRef = useRef(null);
  const bodyRef = useRef(null);
  const eyebrowRef = useRef(null);
  const indicatorRef = useRef(null);
  const particlesRef = useRef([]);
  const textRectsRef = useRef([]);
  const cursorRawRef = useRef({ x: -1000, y: -1000, prevX: -1000, prevY: -1000 });
  // `revealed` always true — canvas + dot grid visible from mount. The entrance
  // fade-in used to be gated by IntersectionObserver, but that was flaky on HMR
  // and added complexity for a subtle effect.
  const revealed = true;
  const [hovering, setHovering] = useState(false);
  const [cursorAngle, setCursorAngle] = useState(0);
  const [cursorXY, setCursorXY] = useState({ x: -1000, y: -1000 });

  // Compute text bounding boxes relative to the canvas.
  const recomputeTextRects = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const canvasRect = canvas.getBoundingClientRect();
    const els = [headlineRef.current, bodyRef.current, eyebrowRef.current, indicatorRef.current];
    textRectsRef.current = els
      .filter(Boolean)
      .map((el) => {
        const r = el.getBoundingClientRect();
        // Pad each rect so particles "bounce" with a halo around the text
        const pad = 18;
        return {
          left: r.left - canvasRect.left - pad,
          top: r.top - canvasRect.top - pad,
          right: r.right - canvasRect.left + pad,
          bottom: r.bottom - canvasRect.top + pad,
          cx: (r.left + r.right) / 2 - canvasRect.left,
          cy: (r.top + r.bottom) / 2 - canvasRect.top,
        };
      });
  }, []);

  // Canvas + particle setup + animation loop
  useEffect(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;
    const ctx = canvas.getContext("2d");

    const fitCanvas = () => {
      const dpr = window.devicePixelRatio || 1;
      const rect = container.getBoundingClientRect();
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      canvas.style.width = `${rect.width}px`;
      canvas.style.height = `${rect.height}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      // Seed particles once we know canvas dimensions
      if (particlesRef.current.length === 0) {
        const colors = [
          "0, 240, 200", // cyan
          "123, 106, 232", // purple
          "154, 173, 23", // green
        ];
        particlesRef.current = Array.from({ length: 32 }, (_, i) => ({
          x: Math.random() * rect.width,
          y: Math.random() * rect.height,
          vx: (Math.random() - 0.5) * 0.7,
          vy: (Math.random() - 0.5) * 0.7,
          size: 1 + Math.random() * 2.4,
          color: colors[i % colors.length],
          phase: Math.random() * Math.PI * 2,
        }));
      }
      // Re-project text rects after any resize
      recomputeTextRects();
    };
    fitCanvas();

    const onResize = () => fitCanvas();
    window.addEventListener("resize", onResize);

    let rafId;
    let time = 0;

    const tick = () => {
      const rect = container.getBoundingClientRect();
      const W = rect.width;
      const H = rect.height;

      ctx.clearRect(0, 0, W, H);
      time += 0.016;

      const rects = textRectsRef.current;
      for (const p of particlesRef.current) {
        // ─── Repulsion from text copy (deconstructive avoidance) ───
        for (const r of rects) {
          // Closest point on the rect to the particle
          const px = Math.max(r.left, Math.min(p.x, r.right));
          const py = Math.max(r.top, Math.min(p.y, r.bottom));
          const dx = p.x - px;
          const dy = p.y - py;
          const distSq = dx * dx + dy * dy;
          const threshold = 70;
          if (distSq < threshold * threshold) {
            const dist = Math.sqrt(distSq) || 0.0001;
            // If the particle is strictly inside the rect, push it outward
            // toward the nearest edge using center-based vector as fallback
            const insideRect = p.x > r.left && p.x < r.right && p.y > r.top && p.y < r.bottom;
            let nx;
            let ny;
            if (insideRect) {
              const cdx = p.x - r.cx;
              const cdy = p.y - r.cy;
              const cmag = Math.sqrt(cdx * cdx + cdy * cdy) || 1;
              nx = cdx / cmag;
              ny = cdy / cmag;
            } else {
              nx = dx / dist;
              ny = dy / dist;
            }
            const force = ((threshold - dist) / threshold) * 0.35;
            p.vx += nx * force;
            p.vy += ny * force;
          }
        }

        // ─── Gentle brownian jitter (keeps the field alive) ────
        p.vx += (Math.random() - 0.5) * 0.025;
        p.vy += (Math.random() - 0.5) * 0.025;

        // ─── Velocity cap + damping ────────────────────────────
        const vmagSq = p.vx * p.vx + p.vy * p.vy;
        const vmax = 1.2;
        if (vmagSq > vmax * vmax) {
          const vmag = Math.sqrt(vmagSq);
          p.vx = (p.vx / vmag) * vmax;
          p.vy = (p.vy / vmag) * vmax;
        }
        p.vx *= 0.985;
        p.vy *= 0.985;

        // ─── Integrate ────────────────────────────────────────
        p.x += p.vx;
        p.y += p.vy;

        // ─── Wrap around edges (soft bounce) ──────────────────
        if (p.x < 2) {
          p.x = 2;
          p.vx = Math.abs(p.vx);
        } else if (p.x > W - 2) {
          p.x = W - 2;
          p.vx = -Math.abs(p.vx);
        }
        if (p.y < 2) {
          p.y = 2;
          p.vy = Math.abs(p.vy);
        } else if (p.y > H - 2) {
          p.y = H - 2;
          p.vy = -Math.abs(p.vy);
        }

        // ─── Draw — subtle glow with breathing alpha ──────────
        const alpha = 0.32 + Math.sin(time * 1.6 + p.phase) * 0.18;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${p.color}, ${alpha})`;
        ctx.shadowColor = `rgba(${p.color}, 0.7)`;
        ctx.shadowBlur = p.size * 4;
        ctx.fill();
        ctx.shadowBlur = 0;
      }

      rafId = requestAnimationFrame(tick);
    };
    tick();

    // Periodically refresh text rects in case fonts load / layout shifts
    const rectInterval = setInterval(recomputeTextRects, 1500);

    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener("resize", onResize);
      clearInterval(rectInterval);
    };
  }, [recomputeTextRects]);

  const handleMove = (e) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    // Delta wing rotation — point in the direction of movement.
    // NOTE: this is the ONLY cursor-driven effect in the section. The field
    // (canvas particles + dot grid + scanlines) is fully independent — it
    // reacts to the copy, not the cursor.
    const prev = cursorRawRef.current;
    const dx = x - prev.x;
    const dy = y - prev.y;
    const speedSq = dx * dx + dy * dy;
    if (speedSq > 4 && prev.x > -999) {
      // Only update angle if moved > 2px and we have a previous position
      const angleDeg = Math.atan2(dy, dx) * (180 / Math.PI) + 90;
      setCursorAngle(angleDeg);
    }
    cursorRawRef.current = { x, y, prevX: prev.x, prevY: prev.y };
    setCursorXY({ x, y });
  };

  const handleEnter = () => {
    setHovering(true);
    recomputeTextRects();
  };

  const handleLeave = () => {
    setHovering(false);
    setCursorXY({ x: -1000, y: -1000 });
    cursorRawRef.current = { x: -1000, y: -1000, prevX: -1000, prevY: -1000 };
  };

  return (
    <section
      ref={containerRef}
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
      onMouseMove={handleMove}
      style={{
        position: "relative",
        padding: "clamp(72px, 10vw, 128px) clamp(24px, 5vw, 80px)",
        borderTop: `1px solid ${t.borderSubtle}`,
        background: `radial-gradient(ellipse at 50% 40%, rgba(123,106,232,0.06) 0%, transparent 60%), linear-gradient(180deg, ${t.bg} 0%, ${t.bgAlt} 50%, ${t.bg} 100%)`,
        overflow: "hidden",
        isolation: "isolate",
        cursor: hovering ? "none" : "default",
      }}
    >
      {/* Quantum dot grid (refraction lattice) */}
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          inset: 0,
          backgroundImage:
            "radial-gradient(circle at 1px 1px, rgba(0,240,200,0.07) 1px, transparent 0)",
          backgroundSize: "26px 26px",
          opacity: revealed ? 1 : 0,
          transition: "opacity 1.6s ease",
          pointerEvents: "none",
        }}
      />

      {/* Animated scanlines */}
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          inset: 0,
          backgroundImage:
            "linear-gradient(180deg, transparent 0px, transparent 2px, rgba(0,240,200,0.035) 2px, rgba(0,240,200,0.035) 3px)",
          backgroundSize: "100% 4px",
          animation: "stealthScanlines 9s linear infinite",
          opacity: 0.6,
          pointerEvents: "none",
        }}
      />

      {/* Edge vignette to fade content edges */}
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          inset: 0,
          background: `radial-gradient(ellipse at center, transparent 35%, ${t.bg} 100%)`,
          pointerEvents: "none",
          opacity: 0.7,
        }}
      />

      {/* Canvas particle field — physics-based, repels text copy */}
      <canvas
        ref={canvasRef}
        aria-hidden="true"
        style={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          pointerEvents: "none",
          opacity: revealed ? 0.85 : 0,
          transition: "opacity 1.5s ease",
          zIndex: 0,
        }}
      />

      {/* Content */}
      <div
        style={{
          position: "relative",
          zIndex: 2,
          maxWidth: 720,
          margin: "0 auto",
          textAlign: "center",
          opacity: revealed ? 1 : 0,
          transform: revealed ? "translateY(0)" : "translateY(20px)",
          transition: "opacity 1s ease 0.3s, transform 1s cubic-bezier(0.22, 1, 0.36, 1) 0.3s",
        }}
      >
        {/* Forthcoming eyebrow chip — muted */}
        <div
          ref={eyebrowRef}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 10,
            padding: "8px 16px",
            border: `1px solid ${BRAND_COLORS.cyan}1f`,
            borderRadius: 100,
            background: `linear-gradient(135deg, ${BRAND_COLORS.cyan}08, transparent)`,
            marginBottom: 32,
          }}
        >
          <span
            style={{
              width: 5,
              height: 5,
              borderRadius: "50%",
              background: `${BRAND_COLORS.cyan}99`,
              boxShadow: `0 0 8px ${BRAND_COLORS.cyan}55`,
              animation: "stealthBreathe 2.2s ease-in-out infinite",
            }}
          />
          <span
            style={{
              fontFamily: "'DM Mono', monospace",
              fontSize: 12,
              letterSpacing: 3,
              textTransform: "uppercase",
              color: `${BRAND_COLORS.cyan}99`,
            }}
          >
            Forthcoming
          </span>
        </div>

        {/* Glitch headline — desaturated for stealth */}
        <h2
          ref={headlineRef}
          style={{
            fontSize: "clamp(36px, 6vw, 64px)",
            fontWeight: 900,
            letterSpacing: -2,
            lineHeight: 1.05,
            color: t.textHigh,
            margin: "0 0 28px",
            animation: "stealthGlitch 7s steps(1, end) infinite",
            willChange: "transform, text-shadow, clip-path",
          }}
        >
          Exiting stealth.
          <br />
          <span style={{ color: t.textFaint, fontWeight: 700 }}>Soon.</span>
        </h2>

        {/* Body — faint */}
        <p
          ref={bodyRef}
          style={{
            fontSize: "clamp(15px, 1.8vw, 18px)",
            lineHeight: 1.85,
            color: t.textLow,
            fontFamily: "Georgia, serif",
            margin: "0 auto 44px",
            maxWidth: 560,
          }}
        >
          We have been working on many things. We promise there is more to come.
        </p>

        {/* Bottom indicator pointing to Our Work — barely there */}
        <div
          ref={indicatorRef}
          style={{
            display: "inline-flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 10,
            color: t.textMid,
            fontSize: 12,
            fontFamily: "'DM Mono', monospace",
            letterSpacing: 3,
            textTransform: "uppercase",
          }}
        >
          <span>Some of our work is shared below</span>
          <span
            aria-hidden="true"
            style={{
              fontSize: 18,
              color: `${BRAND_COLORS.cyan}88`,
              animation: "stealthBreathe 2.4s ease-in-out infinite",
            }}
          >
            ↓
          </span>
        </div>
      </div>

      {/* ─── Delta wing cursor overlay ─────────────────────── */}
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          left: cursorXY.x,
          top: cursorXY.y,
          width: 0,
          height: 0,
          pointerEvents: "none",
          zIndex: 10,
          opacity: hovering ? 1 : 0,
          transition: "opacity 200ms ease",
          transform: `translate(-50%, -50%) rotate(${cursorAngle}deg)`,
          transformOrigin: "center center",
          willChange: "left, top, transform",
        }}
      >
        <svg
          width="44"
          height="44"
          viewBox="0 0 44 44"
          style={{
            display: "block",
            filter: `drop-shadow(0 0 6px ${BRAND_COLORS.cyan}aa)`,
          }}
        >
          {/* Delta wing outline */}
          <polygon
            points="22,3 5,39 39,39"
            fill={`${BRAND_COLORS.cyan}1a`}
            stroke={`${BRAND_COLORS.cyan}d9`}
            strokeWidth="1.5"
            strokeLinejoin="round"
          />
          {/* Center fin / stabilizer */}
          <line
            x1="22"
            y1="12"
            x2="22"
            y2="36"
            stroke={`${BRAND_COLORS.cyan}66`}
            strokeWidth="0.75"
          />
          {/* Cockpit dot */}
          <circle cx="22" cy="17" r="2" fill={`${BRAND_COLORS.cyan}cc`} />
          {/* Trailing edge markers */}
          <line
            x1="12"
            y1="36"
            x2="15"
            y2="32"
            stroke={`${BRAND_COLORS.cyan}55`}
            strokeWidth="0.6"
          />
          <line
            x1="32"
            y1="36"
            x2="29"
            y2="32"
            stroke={`${BRAND_COLORS.cyan}55`}
            strokeWidth="0.6"
          />
        </svg>
      </div>
    </section>
  );
}

